'use client';
'use strict';

var _tslib = require('../../../_virtual/_tslib.js');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
var Avatar = require('../../DataDisplays/Avatar/Avatar.js');
require('../../DataDisplays/Label/Label.js');
var SystemIcon = require('../../DataDisplays/SystemIcon/SystemIcon.js');
var Divider = require('../../DataDisplays/Divider/Divider.js');
var Button = require('../Button/Button.js');
var CheckboxListItem = require('../CheckboxListItem/CheckboxListItem.js');

const InformationBox = ({ userId, imgUrl = '' })=>{
    return jsxRuntime.jsxs(Box, {
        className: "flex items-center gap-2",
        children: [
            jsxRuntime.jsx(Avatar, {
                imgUrl: imgUrl,
                size: "sm"
            }),
            jsxRuntime.jsx(Typography, {
                variant: "body1",
                className: "w-full font-medium text-w-gray-600",
                children: userId
            })
        ]
    });
};
const GuideText = ({ guideText })=>{
    return jsxRuntime.jsx(Box, {
        className: "w-full bg-w-gray-10 text-w-gray-500",
        children: jsxRuntime.jsx("ul", {
            className: "flex flex-col gap-0.5 typo-body2",
            children: typeof guideText === 'string' ? jsxRuntime.jsx("li", {
                className: 'before:mr-2 before:inline-flex before:items-center before:justify-center before:content-["\u2022"]',
                children: guideText
            }) : jsxRuntime.jsx(jsxRuntime.Fragment, {
                children: guideText === null || guideText === void 0 ? void 0 : guideText.map((message)=>jsxRuntime.jsx("li", {
                        className: 'before:mr-2 before:inline-flex before:items-center before:justify-center before:content-["\u2022"]',
                        children: message
                    }, message))
            })
        })
    });
};
/**
 * 댓글 입력 영역 컴포넌트
 * @param {Object} props
 * @param {boolean} [props.isReply] - 답글 모드 여부
 * @param {string|string[]} [props.guideText] - 가이드 텍스트
 * @param {string} props.buttonName - 버튼 텍스트
 * @param {string} props.value - 텍스트 영역 값
 * @param {string} [props.cancelButtonName='취소'] - 취소 버튼 텍스트
 * @param {boolean} [props.isSelected] - 선택 여부
 * @param {string} [props.optionMessage] - 옵션 메시지
 * @param {ReactNode} [props.children] - 커스텀 컨텐츠
 * @param {() => void} [props.onCheck] - 체크박스 변경 핸들러
 * @param {() => void} [props.onClose] - 닫기 핸들러
 * @param {() => void} [props.onSubmit] - 제출 핸들러
 */ const CommentArea = react.forwardRef((_a, ref)=>{
    var { imgUrl, userId, isReply, guideText, buttonName, isSelected, optionMessage, cancelButtonName = '취소', onClose, onSubmit, onCheck } = _a, defaultProps = _tslib.__rest(_a, [
        "imgUrl",
        "userId",
        "isReply",
        "guideText",
        "buttonName",
        "isSelected",
        "optionMessage",
        "cancelButtonName",
        "onClose",
        "onSubmit",
        "onCheck"
    ]);
    const { value, className, disabled, spellCheck = false, children } = defaultProps, props = _tslib.__rest(defaultProps, [
        "value",
        "className",
        "disabled",
        "spellCheck",
        "children"
    ]);
    const isValueEmpty = value.trim() === '';
    const isButtonDisabled = !userId || isValueEmpty || disabled;
    const needCheckList = userId && optionMessage;
    return jsxRuntime.jsxs(Box, {
        "data-testid": "design-system-commentArea--full-container",
        className: tailwindMerge.twMerge('flex w-full items-start gap-4', className),
        children: [
            isReply && jsxRuntime.jsx(SystemIcon, {
                name: "medium_reply",
                className: "shrink-0 text-w-gray-400"
            }),
            jsxRuntime.jsxs(Box, {
                "data-testid": "design-system-commentArea--container",
                className: "w-full rounded-xl border border-w-gray-200 bg-w-white",
                children: [
                    jsxRuntime.jsx(Box, {
                        className: "flex w-full flex-col gap-2 px-4 py-3",
                        children: children ? jsxRuntime.jsx(Box, {
                            className: "h-[98px]",
                            children: children
                        }) : jsxRuntime.jsxs(jsxRuntime.Fragment, {
                            children: [
                                userId && jsxRuntime.jsx(InformationBox, {
                                    imgUrl: imgUrl,
                                    userId: userId
                                }),
                                jsxRuntime.jsx("textarea", Object.assign({
                                    "data-testid": "design-system-commentArea",
                                    ref: ref,
                                    value: value,
                                    spellCheck: spellCheck,
                                    disabled: disabled || !userId,
                                    className: "h-[63px] w-full resize-none border-none bg-transparent tracking-default text-w-gray-900 outline-none placeholder:font-normal placeholder:text-w-gray-300 placeholder:typo-body1"
                                }, props))
                            ]
                        })
                    }),
                    guideText && jsxRuntime.jsx(Box, {
                        "data-testid": "design-system-commentArea--guide-message",
                        className: "w-full bg-w-gray-10 px-4 py-2 text-w-gray-500",
                        children: jsxRuntime.jsx(GuideText, {
                            guideText: guideText
                        })
                    }),
                    jsxRuntime.jsx(Divider, {}),
                    jsxRuntime.jsxs(Box, {
                        "data-testid": "design-system-commentArea--buttons",
                        className: tailwindMerge.twMerge('flex w-full items-center justify-end px-4 py-3', needCheckList && 'justify-between'),
                        children: [
                            needCheckList && jsxRuntime.jsx(CheckboxListItem, {
                                label: optionMessage,
                                checked: isSelected,
                                onChange: onCheck,
                                disabled: disabled || !userId
                            }),
                            jsxRuntime.jsxs(Box, {
                                className: "flex w-fit shrink-0 items-center gap-3",
                                children: [
                                    isReply && jsxRuntime.jsx(Button, {
                                        size: "sm",
                                        onClick: onClose,
                                        color: "gray",
                                        children: cancelButtonName
                                    }),
                                    jsxRuntime.jsx(Button, {
                                        variant: "solid",
                                        size: "sm",
                                        disabled: isButtonDisabled,
                                        onClick: onSubmit,
                                        children: buttonName
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
CommentArea.displayName = 'CommentArea';

module.exports = CommentArea;
